import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ContextTags as ContextTagsT,
  IncidentInput,
  SymptomKind,
} from '@ectopics/shared/schemas';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sheet } from '@/components/ui/sheet';
import { SeveritySlider } from '@/components/ui/severity-slider';
import { Textarea } from '@/components/ui/textarea';
import { ToggleChip } from '@/components/ui/toggle-chip';
import { KindIcon, KIND_BLURB, KIND_LABEL } from '@/components/kind-icon';
import { api } from '@/lib/api';

const SearchSchema = z.object({
  kind: z.enum(['ectopic', 'reflux', 'ibs', 'pain']).optional(),
});

export const Route = createFileRoute('/log')({
  validateSearch: SearchSchema,
  component: LogPage,
});

type Kind = SymptomKind;

const TILES: { kind: Kind; accent: string; iconTone: string }[] = [
  { kind: 'ectopic', accent: 'from-rose-500/15 to-rose-500/0', iconTone: 'text-rose-500' },
  { kind: 'reflux', accent: 'from-amber-500/15 to-amber-500/0', iconTone: 'text-amber-500' },
  { kind: 'ibs', accent: 'from-emerald-500/15 to-emerald-500/0', iconTone: 'text-emerald-500' },
  { kind: 'pain', accent: 'from-violet-500/15 to-violet-500/0', iconTone: 'text-violet-500' },
];

const PAIN_LOCATIONS = ['chest', 'back', 'head', 'neck', 'abdomen', 'joints', 'other'] as const;
type PainLoc = (typeof PAIN_LOCATIONS)[number];

function LogPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [openKind, setOpenKind] = React.useState<Kind | null>(search.kind ?? null);

  React.useEffect(() => {
    if (search.kind && search.kind !== openKind) setOpenKind(search.kind);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.kind]);

  function close() {
    setOpenKind(null);
    if (search.kind) {
      void navigate({ search: {}, replace: true });
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <section>
        <h1 className="text-2xl font-semibold tracking-tight">Quick log</h1>
        <p className="text-sm text-muted-foreground">
          Tap a tile, set severity, save. Works offline — entries sync when you reconnect.
        </p>
      </section>

      <div className="grid grid-cols-2 gap-3">
        {TILES.map((t) => (
          <button
            key={t.kind}
            type="button"
            onClick={() => setOpenKind(t.kind)}
            className={`group relative h-32 overflow-hidden rounded-2xl border border-border bg-gradient-to-br ${t.accent} bg-card p-4 text-left shadow-sm transition-all hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.99]`}
          >
            <div className="flex h-full flex-col justify-between">
              <div className="flex items-center gap-2">
                <KindIcon kind={t.kind} className={`h-6 w-6 ${t.iconTone}`} />
                <span className="text-lg font-semibold tracking-tight">{KIND_LABEL[t.kind]}</span>
              </div>
              <span className="text-xs text-muted-foreground">{KIND_BLURB[t.kind]}</span>
            </div>
          </button>
        ))}
        <button
          type="button"
          onClick={() => navigate({ to: '/rollup' })}
          className="group relative col-span-2 h-24 overflow-hidden rounded-2xl border border-dashed border-border bg-card p-4 text-left shadow-sm transition-all hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.99]"
        >
          <div className="flex h-full flex-col justify-between">
            <span className="text-base font-semibold tracking-tight">End-of-day rollup</span>
            <span className="text-xs text-muted-foreground">
              5 sliders + note. Once per local date.
            </span>
          </div>
        </button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Tips</CardTitle>
          <CardDescription>
            Severity 1 is "barely noticed", 5 is "had to stop what I was doing".
          </CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Editing past entries lands later. For now: log it now, fix the timestamp later if you
          remember it wrong.
        </CardContent>
      </Card>

      {openKind && (
        <IncidentSheet kind={openKind} open={openKind !== null} onClose={close} />
      )}
    </div>
  );
}

type IncidentSheetProps = {
  kind: Kind;
  open: boolean;
  onClose: () => void;
};

function IncidentSheet({ kind, open, onClose }: IncidentSheetProps) {
  const [severity, setSeverity] = React.useState<number>(2);
  const [note, setNote] = React.useState('');
  const [tags, setTags] = React.useState<ContextTagsT>({});
  const [ectopicCount, setEctopicCount] = React.useState<string>('');
  const [refluxLocation, setRefluxLocation] = React.useState<'throat' | 'chest' | 'both' | null>(
    null,
  );
  const [wokeFromSleep, setWokeFromSleep] = React.useState(false);
  const [ibsKind, setIbsKind] = React.useState<'pain' | 'bloating' | 'urgency' | 'stool' | null>(
    null,
  );
  const [bristol, setBristol] = React.useState<number | null>(null);
  const [painLocation, setPainLocation] = React.useState<PainLoc | null>(null);
  const qc = useQueryClient();

  React.useEffect(() => {
    if (!open) return;
    setSeverity(2);
    setNote('');
    setTags({});
    setEctopicCount('');
    setRefluxLocation(null);
    setWokeFromSleep(false);
    setIbsKind(null);
    setBristol(null);
    setPainLocation(null);
  }, [open, kind]);

  const submit = useMutation({
    mutationFn: async () => {
      const occurredAt = new Date().toISOString();
      const base = {
        occurred_at: occurredAt,
        severity,
        note: note.trim() || undefined,
        tags,
      } as const;
      let input: IncidentInput;
      if (kind === 'ectopic') {
        const parsed = ectopicCount.trim() === '' ? undefined : Number(ectopicCount);
        input = {
          ...base,
          kind: 'ectopic',
          ectopic_count: parsed && parsed > 0 && Number.isInteger(parsed) ? parsed : undefined,
        };
      } else if (kind === 'reflux') {
        input = {
          ...base,
          kind: 'reflux',
          reflux_location: refluxLocation ?? undefined,
          woke_from_sleep: wokeFromSleep ? true : undefined,
        };
      } else if (kind === 'ibs') {
        input = {
          ...base,
          kind: 'ibs',
          ibs_kind: ibsKind ?? undefined,
          bristol_scale: bristol ?? undefined,
        };
      } else {
        input = {
          ...base,
          kind: 'pain',
          pain_location: painLocation ?? undefined,
        };
      }
      return api.submitIncident(input);
    },
    onSuccess: (res) => {
      if (res.status === 'rejected') return;
      // Anything that reads incidents or derived trends needs to refetch.
      // Cheap to invalidate broadly — these queries already cache for 60s.
      void qc.invalidateQueries({ queryKey: ['incidents-range'] });
      void qc.invalidateQueries({ queryKey: ['incidents-day'] });
      void qc.invalidateQueries({ queryKey: ['trends'] });
      void qc.invalidateQueries({ queryKey: ['series'] });
      window.setTimeout(onClose, 600);
    },
  });

  const toggleTag = (key: keyof ContextTagsT, on: boolean) => {
    setTags((t) => {
      const next = { ...t };
      if (on) {
        if (key === 'stress') next.stress = next.stress ?? 3;
        else (next as Record<string, unknown>)[key] = true;
      } else {
        delete (next as Record<string, unknown>)[key];
      }
      return next;
    });
  };

  const title = (
    <span className="flex items-center gap-2">
      <KindIcon kind={kind} className="h-5 w-5 text-muted-foreground" />
      Log {KIND_LABEL[kind].toLowerCase()}
    </span>
  );
  const description = new Date().toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()} title={title} description={description}>
      <div className="flex flex-col gap-5">
        <SeveritySlider value={severity} onChange={setSeverity} />

        {kind === 'ectopic' && (
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium" htmlFor="ectopic-count">
              Estimated count (optional)
            </label>
            <input
              id="ectopic-count"
              inputMode="numeric"
              pattern="[0-9]*"
              value={ectopicCount}
              onChange={(e) => setEctopicCount(e.target.value.replace(/\D+/g, ''))}
              placeholder="e.g. 3"
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
        )}

        {kind === 'reflux' && (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium">Where</span>
              <div className="flex flex-wrap gap-2">
                {(['throat', 'chest', 'both'] as const).map((loc) => (
                  <ToggleChip
                    key={loc}
                    pressed={refluxLocation === loc}
                    onClick={() => setRefluxLocation((prev) => (prev === loc ? null : loc))}
                  >
                    {loc}
                  </ToggleChip>
                ))}
              </div>
            </div>
            <ToggleChip
              pressed={wokeFromSleep}
              onClick={() => setWokeFromSleep((v) => !v)}
              className="self-start"
            >
              Woke me from sleep
            </ToggleChip>
          </div>
        )}

        {kind === 'ibs' && (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium">Type</span>
              <div className="flex flex-wrap gap-2">
                {(['pain', 'bloating', 'urgency', 'stool'] as const).map((k) => (
                  <ToggleChip
                    key={k}
                    pressed={ibsKind === k}
                    onClick={() => setIbsKind((prev) => (prev === k ? null : k))}
                  >
                    {k}
                  </ToggleChip>
                ))}
              </div>
            </div>
            {ibsKind === 'stool' && (
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium">Bristol scale</span>
                <div className="grid grid-cols-7 gap-1">
                  {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setBristol((prev) => (prev === n ? null : n))}
                      className={`h-9 rounded-md border text-xs font-medium ${
                        bristol === n
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-input bg-background hover:bg-accent'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {kind === 'pain' && (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">Where</span>
            <div className="flex flex-wrap gap-2">
              {PAIN_LOCATIONS.map((loc) => (
                <ToggleChip
                  key={loc}
                  pressed={painLocation === loc}
                  onClick={() => setPainLocation((prev) => (prev === loc ? null : loc))}
                >
                  {loc}
                </ToggleChip>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium">Context (optional)</span>
          <div className="flex flex-wrap gap-2">
            <ToggleChip
              pressed={!!tags.caffeine}
              onClick={() => toggleTag('caffeine', !tags.caffeine)}
            >
              Caffeine
            </ToggleChip>
            <ToggleChip
              pressed={!!tags.big_meal}
              onClick={() => toggleTag('big_meal', !tags.big_meal)}
            >
              Big meal
            </ToggleChip>
            <ToggleChip
              pressed={!!tags.slept_well}
              onClick={() => toggleTag('slept_well', !tags.slept_well)}
            >
              Slept well
            </ToggleChip>
            <ToggleChip
              pressed={tags.stress !== undefined}
              onClick={() => toggleTag('stress', tags.stress === undefined)}
            >
              Stress
            </ToggleChip>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium" htmlFor="note">
            Note (optional)
          </label>
          <Textarea
            id="note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What were you doing? What did it feel like?"
            maxLength={2000}
          />
        </div>

        {submit.data?.status === 'sent' && (
          <p className="text-sm text-emerald-600">Saved.</p>
        )}
        {submit.data?.status === 'queued' && (
          <p className="text-sm text-amber-600">
            Saved offline. Will sync when reconnected.
          </p>
        )}
        {submit.data?.status === 'rejected' && (
          <p className="text-sm text-destructive">Rejected: {submit.data.error}</p>
        )}
        {submit.error && (
          <p className="text-sm text-destructive">{(submit.error as Error).message}</p>
        )}

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={submit.isPending}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={() => submit.mutate()}
            disabled={submit.isPending}
            className="flex-1"
          >
            {submit.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
